<script lang="ts" setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import type { EffortEntryJsonldDefaultRead } from '@/api'
  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'

  import ChartDataLabels from 'chartjs-plugin-datalabels'
  import type { MenuItem } from 'primevue/menuitem'

  import { useTitrop } from '@/composables/useTitrop.ts'
  import { useGeneratedEffortEntryStore } from '@/stores'
  import { DateTime, Duration } from 'luxon'
  import { useFormatter } from '@/composables/useFormatter.ts'

  const titrop = useTitrop()
  const effortEntries = useGeneratedEffortEntryStore()

  const todaysEffortEntries = ref<EffortEntryJsonldDefaultRead[] | undefined>()

  const loading = ref(true)
  const chartRef = ref()
  const chartContainerRef = ref<HTMLElement>()
  let chartResizeObserver: ResizeObserver | undefined
  const $formatter = useFormatter()
  const { t } = useI18n()
  const sumTimeToday = ref<Duration>(Duration.fromMillis(0))
  const expectedHours = ref<Duration>(Duration.fromDurationLike({ hours: 8 }))
  setInterval(refresh, 1000 * 60 * 2)

  async function refresh() {
    loading.value = true
    const todayStart = DateTime.now().startOf('day').toISO()
    const todayEnd = DateTime.now().endOf('day').toISO()

    todaysEffortEntries.value = await effortEntries
      .findAll(
        {
          query: {
            employee: titrop.value.user.currentIri,
            'timeStart[after]': todayStart,
            'timeEnd[before]': todayEnd
          }
        },
        { forceRefresh: true }
      )
      .then((d) => d.value)
    if (!todaysEffortEntries.value || !todaysEffortEntries.value.length) sumTimeToday.value = Duration.fromMillis(0)
    else {
      sumTimeToday.value = todaysEffortEntries.value.reduce((sum, ee) => {
        return sum.plus(Duration.fromISO(ee.duration ?? 'PT0H'))
      }, Duration.fromMillis(0))
    }
    loading.value = false
  }

  onMounted(async () => {
    await refresh()
    await nextTick()

    if (chartContainerRef.value) {
      chartResizeObserver = new ResizeObserver(() => {
        chartRef.value?.getChart()?.resize()
      })
      chartResizeObserver.observe(chartContainerRef.value)
    }
  })

  onBeforeUnmount(() => {
    chartResizeObserver?.disconnect()
  })

  const documentStyle = getComputedStyle(document.body)

  const allLabels = computed<{ label: string; value: number; color: string; hover: string }[]>(() => [
    {
      label: t('booked'),
      value: sumTimeToday.value.as('hours'),
      color: documentStyle.getPropertyValue('--p-green-500'),
      hover: documentStyle.getPropertyValue('--p-green-400')
    },
    {
      label: t('notBooked'),
      value: Math.max(0, expectedHours.value.minus(sumTimeToday.value).as('hours')),
      color: documentStyle.getPropertyValue('--p-red-500'),
      hover: documentStyle.getPropertyValue('--p-red-400')
    }
  ])

  const withData = computed(() => allLabels.value.filter((x) => x.value > 0))
  const data = computed(() => withData.value.map((x) => x.value))
  const labels = computed(() => withData.value.map((x) => x.label))

  const chartOptions = ref({
    maintainAspectRatio: false,
    responsive: true,
    animation: false,
    cutout: '52%',
    layout: {
      padding: 4
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    }
  })
  const chartData = computed(() => ({
    labels: labels.value,
    datasets: [
      {
        datalabels: {
          formatter: (value: number) => t('hours', { value: $formatter.NumberFixed(value, 1) }),
          color: 'white',
          labels: {
            value: {
              color: 'white',
              font: {
                weight: 'bold',
                size: 14
              },
              textShadowBlur: 3,
              textShadowColor: 'black',
              textStrokeWidth: 0,
              textStrokeColor: documentStyle.getPropertyValue('--p-gray-900')
            }
          }
        },
        data: data.value,
        backgroundColor: withData.value.map((a) => a.color),
        hoverBackgroundColor: withData.value.map((a) => a.hover)
      }
    ]
  }))

  const contextMenuRef = ref()
  const contextMenuItems: MenuItem[] = [
    {
      label: t('recalculate'),
      command: async () => {
        await refresh()
      }
    }
  ]
</script>

<template>
  <WidgetSkeleton v-if="loading" variant="chart" />
  <div v-else-if="new Date().getDay() == 6 || new Date().getDay() == 0">
    <Message
      :closable="false"
      class="justify-center"
      severity="secondary">
      {{ t('weekend') }}
    </Message>
  </div>
  <div
    v-else
    class="today-hours-widget">
    <div class="today-hours-legend">
      <div
        v-for="item in withData"
        :key="item.label"
        class="today-hours-legend-item">
        <span
          :style="{ backgroundColor: item.color }"
          class="today-hours-legend-marker" />
        <span>{{ item.label }}</span>
      </div>
    </div>

    <div
      ref="chartContainerRef"
      class="today-hours-chart">
      <Chart
        ref="chartRef"
        :data="chartData"
        :options="chartOptions"
        :plugins="[ChartDataLabels]"
        class="w-full h-full"
        type="doughnut"
        @contextmenu.prevent="contextMenuRef.show($event)" />
    </div>

    <ContextMenu
      ref="contextMenuRef"
      :model="contextMenuItems" />
  </div>
</template>

<style scoped>
  .today-hours-widget {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    gap: 0.5rem;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    padding: 0.25rem 0.5rem 0.5rem;
    overflow: hidden;
  }

  .today-hours-legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem 0.75rem;
    min-width: 0;
    color: var(--p-text-muted-color);
    font-size: 0.75rem;
  }

  .today-hours-legend-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
  }

  .today-hours-legend-marker {
    flex: 0 0 auto;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
  }

  .today-hours-chart {
    position: relative;
    align-self: center;
    justify-self: center;
    width: min(100%, 18rem);
    height: 100%;
    max-height: 18rem;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  :deep(.today-hours-chart canvas) {
    max-width: 100% !important;
    max-height: 100% !important;
  }
</style>

<i18n>
{
  "de": {
    "booked": "Gebucht",
    "notBooked": "Nicht gebucht",
    "recalculate": "Neu berechnen",
    "weekend": "Wochenende",
    "hours": "{value} Std."
  },
  "en": {
    "booked": "Booked",
    "notBooked": "Not booked",
    "recalculate": "Recalculate",
    "weekend": "Weekend",
    "hours": "{value} hrs"
  }
}
</i18n>
